import { observer } from 'mobx-react-lite';
import { Callout, Target, DirectionalHint, ICalloutProps } from '@fluentui/react/lib/Callout';
import { FocusTrapZone } from '@fluentui/react/lib/FocusTrapZone';
import { lazyDeleteCalendarEventWithConfirm } from 'owa-calendar-event-actions-with-confirm';
import { isEventAMeeting } from 'owa-calendar-event-capabilities';
import { respondToCalendarEvent } from 'owa-calendar-events-actions';
import { getFullCalendarEvent } from 'owa-calendar-forms-common/lib/helpers/eventStore';
import { showFullCompose } from 'owa-calendar-full-compose/lib/actions/showFullCompose';
import { lazyConfirmCloseNotesEditorIfNeededAndContinue } from 'owa-meeting-forms-common';
import { getStore as getPeekStore, closeItemPeek } from 'owa-calendar-peek';
import { CalendarItemPeek } from 'owa-calendar-peek-view';
import { tryShowQuickCompose } from 'owa-calendar-quick-compose/lib/actions/tryShowQuickCompose';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { getUserMailboxInfo } from 'owa-client-ids';
import type EventScope from 'owa-service/lib/contract/EventScope';
import type ResponseTypeType from 'owa-service/lib/contract/ResponseTypeType';
import { shouldShowDeclinedEvents } from 'owa-show-declined-meetings-user-config';
import * as React from 'react';
import type { ViewType } from 'owa-calendar-actions';
import type { ReadingPaneSection } from 'owa-calendar-helpers-types/lib/ReadingPaneSection';

const styles = require('./CalendarItemPeekCallout.scss');

export interface CalendarItemPeekCalloutHandle {
    onCalloutDismiss(): void;
}

/**
 * Props for item peek renderer
 */
export interface CalendarItemPeekCalloutProps extends ICalloutProps, ItemPeekContentProps {
    shouldCoverTarget?: (itemId: string) => boolean;
    onDismissed?: () => void;
    bounds?: ClientRect;
    target: Target;
}

interface ItemPeekContentProps {
    viewType: ViewType;
    entrySource: string;
    onOpenEventClicked: (
        ev?: React.MouseEvent<HTMLElement>,
        scrollToSection?: ReadingPaneSection
    ) => void;
    onEditEventClicked?: () => void;
    onEventDeleted?: () => void;
    onReviewProposedNewTimeClicked: (newEvent?: Partial<CalendarEvent>) => void;
    target: Target;
}

/**
 * Component for rendering item peek
 */
export default observer(
    function CalendarItemPeekCallout(
        props: CalendarItemPeekCalloutProps,
        ref: React.Ref<CalendarItemPeekCalloutHandle>
    ) {
        const {
            bounds,
            target,
            onEditEventClicked,
            onEventDeleted,
            onOpenEventClicked,
            onReviewProposedNewTimeClicked,
            viewType,
            entrySource,
            shouldCoverTarget,
            onDismissed,
            ...calloutProps
        } = props;

        const onDismiss = (ev?: any) => {
            onDismissed?.();
            onCalloutDismiss(ev);
        };

        React.useEffect(() => {
            return () => {
                // if callout was closed forcibly when this component is unmounting, can skip firing the close callback, so clean up here
                onDismiss(null);
            };
        }, []);

        React.useImperativeHandle(
            ref,
            () => ({
                onCalloutDismiss() {
                    onDismiss();
                },
            }),
            []
        );

        const { itemId: id = undefined } = getPeekStore().viewState || {};
        const itemPeekStore = getPeekStore();
        if (!itemPeekStore.viewState || !target) {
            return null;
        }
        return (
            <Callout
                className={styles.peekCallout}
                target={target}
                directionalHint={DirectionalHint.rightCenter}
                setInitialFocus={true}
                onDismiss={onDismiss}
                bounds={bounds}
                coverTarget={shouldCoverTarget?.(id)}
                shouldDismissOnWindowFocus={false}
                {...calloutProps}>
                <ItemPeekContent
                    viewType={viewType}
                    entrySource={entrySource}
                    onOpenEventClicked={onOpenEventClicked}
                    onEditEventClicked={onEditEventClicked}
                    onEventDeleted={onEventDeleted}
                    onReviewProposedNewTimeClicked={onReviewProposedNewTimeClicked}
                    target={target as HTMLDivElement}
                />
            </Callout>
        );
    },
    { forwardRef: true }
);

// create inner component so prop changes that only affect the Callout don't need to recompute everything
const ItemPeekContent = observer(function ItemPeekContent({
    viewType,
    entrySource,
    onOpenEventClicked,
    onEditEventClicked,
    onEventDeleted,
    onReviewProposedNewTimeClicked,
    target,
}: ItemPeekContentProps) {
    const { itemId: id = undefined } = getPeekStore().viewState || {};
    const showDeclinedEvents = shouldShowDeclinedEvents();

    const onEditClicked = React.useCallback(
        (eventScope: EventScope) => {
            const item = getFullCalendarEvent(null, id);
            onEditEventClicked?.();
            if (isEventAMeeting(item)) {
                showFullCompose(item, entrySource, eventScope);
            } else {
                tryShowQuickCompose(item, entrySource, eventScope, target as HTMLDivElement);
            }
        },
        [onEditEventClicked, id, entrySource]
    );

    // need closeItemPeek in wrapped callback for confirm as users can cancel delete and peek should stay open
    const onEventDeletedCallback = React.useCallback(() => {
        onEventDeleted?.();
        closeItemPeek();
    }, [onEventDeleted]);

    const onDeleteEventClicked = React.useCallback(
        (eventScope: EventScope) => {
            const item = getFullCalendarEvent(null, id);
            lazyDeleteCalendarEventWithConfirm.importAndExecute(
                item,
                eventScope,
                false /* isItemSeriesMaster */,
                viewType,
                false /*isInPopOutWindow*/,
                null /*onCancel*/,
                onEventDeletedCallback
            );
        },
        [id, onEventDeletedCallback, viewType]
    );

    const onRespondEventClicked = React.useCallback(
        (
            newResponseType: ResponseTypeType,
            eventScope: EventScope,
            sendMessage: boolean,
            notes: string
        ) => {
            const item = getFullCalendarEvent(null, id);
            respondToCalendarEvent(
                {
                    Id: id,
                    mailboxInfo: null,
                },
                item.ResponseType,
                newResponseType,
                item.FreeBusyType,
                eventScope,
                sendMessage,
                viewType,
                notes,
                undefined /* proposeNewTime */,
                showDeclinedEvents /* showDeclinedEvents */
            );
            closeItemPeek();
        },
        [id, showDeclinedEvents, viewType]
    );

    const itemId = {
        Id: id,
        mailboxInfo: null,
    };

    return (
        <FocusTrapZone
            isClickableOutsideFocusTrap={true}
            forceFocusInsideTrap={false}
            className={styles.flexContainer}>
            <CalendarItemPeek
                key={itemId.Id}
                itemId={itemId}
                onOpenEventClicked={onOpenEventClicked}
                onEditClicked={onEditClicked}
                onDeleteClicked={onDeleteEventClicked}
                onRespondClicked={onRespondEventClicked}
                onReviewProposedNewTimeClicked={onReviewProposedNewTimeClicked}
            />
        </FocusTrapZone>
    );
});

function onCalloutDismiss(ev?: any) {
    if (!getPeekStore().viewState) {
        return;
    }
    const { itemId } = getPeekStore().viewState;
    const ClientItemId = {
        Id: itemId,
        mailboxInfo: getUserMailboxInfo(),
    };
    // Dismiss peek unless the focus target is part of persona live card.
    var hasLPC =
        document.getElementById('LivePersonaCardRootElement')?.children.length > 0 ||
        document.getElementById('ImmersiveProfileOverlayWrapper')?.children.length > 0;
    var modal = document.getElementsByClassName('ms-Modal');
    if (
        !hasLPC &&
        (modal.length == 0 || (modal.length > 0 && ev && !modal[0].contains(ev.target)))
    ) {
        lazyConfirmCloseNotesEditorIfNeededAndContinue.importAndExecute(
            ClientItemId,
            () => closeItemPeek(),
            true /* forceClose */
        );
    }
}
